import React, { useState } from "react";
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, MenuItem, Link, FormControlLabel, Checkbox, Tooltip } from '@material-ui/core';
import { Draw } from "./RoundOverviewModels";
import { Fixture, FixtureTeam } from "../TeamOverview/TeamOverviewModels";
import { min } from "moment";
import CreateRoundUtil from "../CreateRound/CreateRoundUtil";
import FixtureOverviewUtil from "../FixtureOverview/FixtureOverviewUtil";
import DateTimePickerX, { DateTimePickerXValue, getDateTimePickerXValue } from "../CreateFixture/DateTimePickerX";
import RoundOverviewUtil from "./RoundOverviewUtil";
import DrawRulesDialog from "./DrawRulesDialog";

interface FixtureDialogProps {
    open: boolean;
    fixture: Fixture;
    /** Only set when the fixture is opened from a round. */
    draw?: Draw | null;
    onClose: (fixture: Fixture | undefined) => void;
    nameLinkEnabled?: boolean;
    openLabel?: string;
    /** Initial state of the Extended check box, which unlocks name and starting at. Defaults to true. */
    extended?: boolean;
}
interface FixtureDialogState {
    name: string;
    startingAt: DateTimePickerXValue;
    status: string;
    homeScore: string;
    awayScore: string;
    updateMode: string;
}

const toTeamUpdate = (team: FixtureTeam, score: number) => {
    return { teamId: team.id.toString(), teamName: team.name, score: score };
}

const FixtureDialog: React.FC<FixtureDialogProps> = (props) => {
    const getDefaultFormState = (f: Fixture) : FixtureDialogState => {
        return {
            name: f.name ?? "",
            startingAt: getDateTimePickerXValue(new Date(f.startingAt)),
            status: f.status ?? "",
            homeScore: f.homeTeam ? f.homeTeam.score.toString() : "0",
            awayScore: f.awayTeam ? f.awayTeam.score.toString() : "0",
            updateMode: f.updateMode ?? ""
        };
    }
    
    const [open, setOpen] = useState<boolean>(false);
    const [extended, setExtended] = useState<boolean>(props.extended !== false);
    const [fixture, setFixture] = useState<Fixture>(props.fixture);
    const [formState, setFormState] = useState<FixtureDialogState>(() => getDefaultFormState(props.fixture));
    const [saveError, setSaveError] = useState<string>("");
    const [saveStatus, setSaveStatus] = useState<number>(0);
 
    const handleClickOpen = () => {
        setOpen(true);
        setSaveError("");
        setSaveStatus(0);
        setExtended(props.extended !== false);
        setFixture(props.fixture);
        setFormState(getDefaultFormState(props.fixture));
    };

    const handleClose = () => {
        setOpen(false);
        if (props.onClose) {
            props.onClose(undefined);
        }
    };
    const handleChange = (name: string, value:string) =>{
        //var prevValue = formState.[name];
        var fs = {...formState, [name]: value};
        setFormState(fs);
    }
    const onChangeStartingAt = (value: DateTimePickerXValue) => {
        setFormState({ ...formState, startingAt: value });
    }
    const onChangeExtended = (checked: boolean) => {
        setExtended(checked);
        if (!checked) {
            // Drop the locked edits so they cannot be saved by accident.
            setFormState({ ...formState, name: fixture.name, startingAt: getDateTimePickerXValue(new Date(fixture.startingAt)) });
        }
    }

    const onSave = () => {
        const methodName = "SaveFixture";
        var name = undefined;
        var startingAt = undefined;
        var status = undefined;
        var homeTeam = undefined;
        var awayTeam = undefined;
        var updateMode = undefined;
        var changeCount = 0;
        setSaveError("");
        setSaveStatus(0);
        if (!fixture) {
            setSaveError("Fixture is null");
            return;
        }
        var fixtureName = formState.name.trim();
        if (fixtureName.length <= 0) {
            setSaveError("Invalid Name, must not be empty");
            return;
        }
        if (fixtureName !== fixture.name) {
            name = fixtureName;
            changeCount++;
        }
        var startingAtValue = formState.startingAt.dateTime;
        if (!startingAtValue) {
            setSaveError("Invalid Starting at");
            return;
        }
        if (startingAtValue.getTime() !== new Date(fixture.startingAt).getTime()) {
            startingAt = startingAtValue;
            changeCount++;
        }
        if (formState.status !== fixture.status) {
            status = formState.status;
            changeCount++;
        }
        if (formState.updateMode !== fixture.updateMode) {
            updateMode = formState.updateMode;
            changeCount++;
        }
        var homeScore = parseInt(formState.homeScore);
        if (isNaN(homeScore) || homeScore < 0) {
            setSaveError("Invalid Home score, must be 0-n");
            return;
        }
        if (homeScore !== fixture.homeTeam.score) {
            homeTeam = toTeamUpdate(fixture.homeTeam, homeScore);
            changeCount++;
        }
        var awayScore = parseInt(formState.awayScore);
        if (isNaN(awayScore) || awayScore < 0) {
            setSaveError("Invalid Away score, must be 0-n");
            return;
        }
        if (awayScore !== fixture.awayTeam.score) {
            awayTeam = toTeamUpdate(fixture.awayTeam, awayScore);
            changeCount++;
        }
        if (changeCount <= 0) {
            setSaveError("No changes");
            return;

        }
        setSaveStatus(1);
        var updateJson = JSON.stringify({name: name, startingAt: startingAt, status: status, homeTeam: homeTeam, awayTeam: awayTeam, updateMode: updateMode});
        FixtureOverviewUtil.updateFixture(fixture.id, updateJson).then(resp => {
            setSaveStatus(0);
          if (resp.success){
            if (resp.item) {
              setFixture(resp.item);
              setOpen(false);
              if (props.onClose) {
                  props.onClose(resp.item);
              }
              return;
            } 
            setSaveError(methodName + " failure: fixture was not returned");

          } else {
            setSaveError(resp.message);
          }
        }).catch(error => {
            setSaveError(`${methodName} error: ${error}`);
            setSaveStatus(0);
        });
           
    };

    var valid = true;
    var nameErr = "";
    if (formState.name.trim().length === 0){
        valid = false;
        nameErr = "Required";
    }

    var startingAtErr = "";
    if (!formState.startingAt.dateTime){
        valid = false;
        startingAtErr = "Required";
    }

    var updateModeErr = "";
    if (formState.updateMode.length === 0){
        valid = false;
        updateModeErr = "Required";
    }

    var statusErr = "";
    if (formState.status.length === 0){
        valid = false;
        statusErr = "Required";
    }
    
    var homeScoreErr = "";
    var homeScore = parseInt(formState.homeScore);
    if (formState.homeScore.length > 0) {
        if (isNaN(homeScore)) {
            valid = false;
            homeScoreErr = "Invalid int";
        } else if (homeScore < 0) {
            valid = false;
            homeScoreErr = "Not in range 0-n";
        }
    } else {
        valid = false;
        homeScoreErr = "Required";
    }

    var awayScoreErr = "";
    var awayScore = parseInt(formState.awayScore);
    if (formState.awayScore.length > 0) {
        if (isNaN(awayScore)) {
            valid = false;
            awayScoreErr = "Invalid int";
        } else if (awayScore < 0) {
            valid = false;
            awayScoreErr = "Not in range 0-n";
        }
    } else {
        valid = false;
        awayScoreErr = "Required";
    }
    var changed = false;
    if (fixture && valid) {
        if (formState.name.trim() !== fixture.name) {
            changed = true;
        } else if (formState.startingAt.dateTime?.getTime() !== new Date(fixture.startingAt).getTime()) {
            changed = true;
        } else if (formState.updateMode !== fixture.updateMode) {
            changed = true;
        } else if (formState.status !== fixture.status) {
            changed = true;
        } else if (homeScore !== fixture.homeTeam.score) {
            changed = true;
        } else if (awayScore !== fixture.awayTeam.score) {
            changed = true;
        }
    }
    var saveEnabled = (valid && saveStatus === 0 && changed);
    var saveErrorBox = null;
    if (saveError.length > 0) {
        saveErrorBox = (<Box color="red">{saveError}</Box>);
    }
    var startingAtField = null;
    if (extended) {
        startingAtField = (
            <Box marginTop={1}>
                <DateTimePickerX label="Starting at" value={formState.startingAt} errorText={startingAtErr} onChange={onChangeStartingAt} />
            </Box>);
    }
    // A fixture opened outside a round has no draw.
    var drawField = null;
    if (props.draw) {
        drawField = (
            <TextField
                label="Draw"
                name="drawId"
                value={props.draw.id}
                helperText={`${props.draw.adminPickedOutcome} (${props.draw.status})`}
                InputProps={{
                readOnly: true,
                }}
                style={{marginLeft:30}}
            />);
    }
    var openButton = null;
    if (props.nameLinkEnabled) {
        openButton = (<Link underline="hover" onClick={handleClickOpen} style={{ cursor: 'pointer' }}>
                {props.fixture.name}
            </Link>);
    } else {
        openButton = (<Button style={{textTransform:"none"}} onClick={handleClickOpen} color="secondary">
                    {props.openLabel ? props.openLabel : "Fixture"}
            </Button>);

    }
    return (
        <span>
            {openButton}
        <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">Fixture</DialogTitle>
            <DialogContent dividers>
                {/* <DialogContentText>
                    To subscribe to this website, please enter your email address here. We will send updates
                    occasionally.
                </DialogContentText> */}
                <Box display="flex" alignItems="center">
                    <TextField
                        label="Fixture"
                        name="name"
                        value={formState.name}
                        InputProps={{
                        readOnly: !extended,
                        }}
                        helperText={nameErr.length > 0 ? nameErr : fixture.id + " | " + CreateRoundUtil.toGameDateString(fixture.startingAt) + " | " + (fixture.leagueTitle ? fixture.leagueTitle : fixture.leagueName)}
                        error={nameErr.length > 0}
                        onChange={e => handleChange("name", e.target.value)}
                        style={{width:400}}
                    />
                    {drawField}
                </Box>
                {startingAtField}
                <Box marginTop={1} width="250px">
                    <TextField
                        label="Update mode"
                        select
                        name="updateMode"
                        value={formState.updateMode}
                        onChange={e => handleChange("updateMode", e.target.value)}
                        fullWidth
                        helperText={updateModeErr}
                        error={updateModeErr.length > 0}
                        autoFocus
                    >
                        {RoundOverviewUtil.updateModes.map(s => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                        ))}
                    </TextField>
                </Box> 
                <Box marginTop={1} width="250px">
                    <TextField
                        label="Status"
                        select
                        name="status"
                        value={formState.status}
                        onChange={e => handleChange("status", e.target.value)}
                        fullWidth
                        helperText={statusErr}
                        error={statusErr.length > 0}
                    >
                        {RoundOverviewUtil.fixtureStatuses.map(s => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box marginTop={1} display="flex" alignItems="center">
                <TextField
                    autoFocus
                    margin="dense"
                    name="homeScore"
                    value={formState.homeScore}
                    label="Home"
                    type="number"
                    onChange={e => handleChange("homeScore", e.target.value)}
                    helperText={homeScoreErr}
                    error={homeScoreErr.length > 0}
                    />&nbsp;&nbsp;
                <TextField
                    autoFocus
                    margin="dense"
                    name="awayScore"
                    value={formState.awayScore}
                    label="Away"
                    type="number"
                    onChange={e => handleChange("awayScore", e.target.value)}
                    helperText={awayScoreErr}
                    error={awayScoreErr.length > 0}
                    />

                </Box>
                {saveErrorBox} 
            </DialogContent>
            <DialogActions>
                <Tooltip title="Also allow editing of Name and Starting at">
                    <FormControlLabel
                        style={{ marginRight: "auto" }}
                        control={<Checkbox checked={extended} name="extended" color="primary" onChange={e => onChangeExtended(e.target.checked)} />}
                        label="Extended"
                    />
                </Tooltip>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={onSave} color="primary" disabled={!saveEnabled}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
        </span>
    );
};

export default FixtureDialog;
